import { GameScene } from '../GameScene';
import { ClientMessageType, type NpcTemplateListMessage } from '@shared/types/messages';
import { getNPCTemplate } from '@shared/data/npcs';

/**
 * Panel de invocación de NPCs (dev): pide la lista de templates al server (requestNpcTemplates),
 * la recibe y la vuelca al panel (handleNpcTemplateListMessage/refreshInvokePanelContents), maneja
 * la selección de fila (handleInvokeListClick) y dispara la invocación (sendInvokeNpcCommand). Incluye
 * los helpers de preview de cabeza/cuerpo del player local que usan las galerías. Extraído de
 * GameScene (god-object). El estado (invokePanel/invokeNpcTemplates) vive en GameScene y se accede vía
 * `this.scene.*`. Hermano de DevPanelsController.
 */
export class InvokeController {
  constructor(private scene: GameScene) {}

  public requestNpcTemplates(): void {
    if (!this.scene.colyseusClient) return;

    this.scene.colyseusClient.send({
      type: ClientMessageType.REQUEST_NPC_TEMPLATES,
    } as never);
  }

  public handleNpcTemplateListMessage(message: NpcTemplateListMessage): void {
    // Ordenar para que en el panel aparezcan PRIMERO los NPCs amigables (no
    // hostile) y DESPUÉS los hostiles, ambos alfabéticamente por nombre.
    // El flag `hostile` se lee del shared template local (no viaja en el msg).
    const sorted = [...message.templates].sort((a, b) => {
      const aHostile = getNPCTemplate(a.id)?.hostile === true;
      const bHostile = getNPCTemplate(b.id)?.hostile === true;
      if (aHostile !== bHostile) return aHostile ? 1 : -1; // friendly primero
      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
    this.scene.invokeNpcTemplates = sorted;
    // Resetear selección al re-recibir lista para evitar apuntar a una posición
    // stale si el orden cambió (ej. nuevo template insertado en el medio).
    this.scene.invokeSelectedIndex = -1;

    if (this.scene.invokeIsOpen) {
      this.refreshInvokePanelContents();
    }
  }

  public getLocalPlayerHeadIdForPreview(): number {
    if (!this.scene.localSessionId) return 1;
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    const headKey = render?.head?.texture?.key;
    if (headKey) {
      const match = /^head(\d+)/i.exec(headKey);
      if (match) {
        const parsed = Number(match[1]);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return 1;
  }

  public getLocalPlayerBodyIdForPreview(): number {
    if (!this.scene.localSessionId) return 1;
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    const bodyKey = render?.sprite?.texture?.key;
    if (bodyKey) {
      const match = /^body(\d+)/i.exec(bodyKey);
      if (match) {
        const parsed = Number(match[1]);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return 1;
  }

  /**

   * Mueve todos los elementos del panel de invocar a una nueva posición

   */


  public handleInvokeListClick(rowIndex: number): void {
    const entry = this.scene.invokeDisplayList[rowIndex];
    if (!entry || entry.type === 'header') return; // headers no son seleccionables
    this.scene.invokeSelectedIndex = entry.templateIndex;
    this.refreshInvokePanelContents();
  }

  public refreshInvokePanelContents(): void {
    if (!this.scene.invokePanel) return;

    // Auto-seleccionar el primer template amigable si no hay nada seleccionado.
    if (this.scene.invokeNpcTemplates.length > 0 && this.scene.invokeSelectedIndex < 0) {
      this.scene.invokeSelectedIndex = 0;
    }

    const selected =
      this.scene.invokeSelectedIndex >= 0
        ? this.scene.invokeNpcTemplates[this.scene.invokeSelectedIndex]
        : undefined;

    // Construir displayList con dos secciones (Amigables / Hostiles). Como los
    // templates ya vienen ordenados (handleNpcTemplateListMessage), basta con
    // detectar el primer hostile y dividir.
    const firstHostileIdx = this.scene.invokeNpcTemplates.findIndex(
      (t: NpcTemplateListMessage['templates'][number]) =>
        getNPCTemplate(t.id)?.hostile === true
    );
    const friendlyCount =
      firstHostileIdx === -1 ? this.scene.invokeNpcTemplates.length : firstHostileIdx;

    const displayList: typeof this.scene.invokeDisplayList = [];
    const listEntries: import('../ui/InvokePanel').InvokeListEntry[] = [];

    if (friendlyCount > 0) {
      displayList.push({ type: 'header', label: 'NPCs Amigables' });
      listEntries.push({ type: 'header', label: 'NPCs Amigables' });
      for (let i = 0; i < friendlyCount; i += 1) {
        displayList.push({ type: 'item', templateIndex: i });
        listEntries.push({ type: 'item', name: this.scene.invokeNpcTemplates[i].name });
      }
    }
    if (friendlyCount < this.scene.invokeNpcTemplates.length) {
      displayList.push({ type: 'header', label: 'NPCs Hostiles' });
      listEntries.push({ type: 'header', label: 'NPCs Hostiles' });
      for (let i = friendlyCount; i < this.scene.invokeNpcTemplates.length; i += 1) {
        displayList.push({ type: 'item', templateIndex: i });
        listEntries.push({ type: 'item', name: this.scene.invokeNpcTemplates[i].name });
      }
    }
    this.scene.invokeDisplayList = displayList;

    // Mapear el templateIndex seleccionado a un rowIndex en el displayList.
    let selectedRowIndex = -1;
    if (this.scene.invokeSelectedIndex >= 0) {
      selectedRowIndex = displayList.findIndex(
        (e) => e.type === 'item' && e.templateIndex === this.scene.invokeSelectedIndex
      );
    }

    const detail = selected
      ? `ID: ${selected.id}\n` +
        `Body: ${selected.bodyId}\n` +
        `Head: ${selected.headId}\n` +
        `Weapon: ${selected.weaponId}\n` +
        `Shield: ${selected.shieldId}\n` +
        `Helm: ${selected.helmId}`
      : 'Selecciona un NPC';

    this.scene.invokePanel.refreshList(
      listEntries,
      selectedRowIndex,
      detail,
      selected?.name ?? ''
    );

    if (selected) {
      this.scene.invokePanel.refreshPreview(
        selected.bodyId,
        selected.headId,
        selected.shieldId,
        selected.weaponId
      );
    } else {
      this.scene.invokePanel.refreshPreview(0, 0, 0, 0);
    }
  }


  public sendInvokeNpcCommand(): void {
    if (!this.scene.colyseusClient) return;

    if (

      !this.scene.invokeNpcTemplates ||

      this.scene.invokeSelectedIndex < 0 ||

      this.scene.invokeSelectedIndex >= this.scene.invokeNpcTemplates.length

    ) {
      return;
    }

    const tmpl = this.scene.invokeNpcTemplates[this.scene.invokeSelectedIndex];

    this.scene.chat.addSystemMessage(`Invocando NPC template ${tmpl.id} (${tmpl.name})`);

    this.scene.colyseusClient.send({
      type: ClientMessageType.INVOKE_NPC,

      npcTemplateId: tmpl.id,
    } as never);
  }

}
